'use client';

import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { positionWorkspaceMentionMenu } from './workspace-image-mention-menu-position';

/**
 * Keeps the picker below the actual @ character across wrapping, resizing
 * and scrolling. The menu is positioned independently from the prompt model.
 */
export function useWorkspaceImageMentionMenuAnchor(params: {
  rootRef: RefObject<HTMLDivElement | null>;
  editorRef: RefObject<HTMLDivElement | null>;
  activeQuery: { start: number; end: number; query: string } | null;
  optionCount: number;
}) {
  const { rootRef, editorRef, activeQuery, optionCount } = params;
  const menuRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!activeQuery || optionCount === 0) return;
    const root = rootRef.current;
    const editor = editorRef.current;
    const menu = menuRef.current;
    if (!root || !editor || !menu) return;

    const position = () => positionWorkspaceMentionMenu({ root, editor, menu });
    position();

    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    window.visualViewport?.addEventListener('scroll', position);

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(position)
        : null;
    observer?.observe(root);
    observer?.observe(editor);

    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
      window.visualViewport?.removeEventListener('scroll', position);
      observer?.disconnect();
    };
  }, [
    rootRef,
    editorRef,
    activeQuery?.start,
    activeQuery?.end,
    activeQuery?.query,
    optionCount,
  ]);

  return menuRef;
}
